import React, { useEffect, useRef } from "react";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { closeDialog } from "../../components/dialog/dialogSlice";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  Grid as KendoGrid,
  GridColumn as Column,
  GridCustomFilterCellProps,
} from "@progress/kendo-react-grid";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { LoadingPanel } from "../../components/layout/Loading";
import { checkAcessRights } from "../../_helper/helper";
import { EXCEL_EXPORT_ACCESS } from "../../_contstants/common";
import { setCurrentPage, setPageLimit } from "../pagination/paginationSlice";
import { getAllB2BInquiries } from "./services/b2bInquiry.services";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import moment from "moment";
import { Button } from "@progress/kendo-react-buttons";
import CommonFilterCell from "../../components/common/GlobalCommonFilterCell";
import { clearFilterB2BInquiry, setFilterB2BInquiry } from "./b2bInquirySlice";

const B2BInquiry: React.FC = () => {
  const isExcelExportAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS,
  );
  const _export = React.useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  return (
    <>
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 0 20px 0",
          }}
        >
          <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
            B2B Rate Inquiry List
          </Typography.h4>
          <div>
            {isExcelExportAccess && (
              <Button
                title="Export Excel"
                themeColor={"primary"}
                type="button"
                onClick={excelExport}
                style={{ marginRight: 10 }}
              >
                Export to Excel
              </Button>
            )}
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <B2BInquiryGridComponent ref={_export} />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

const B2BInquiryGridComponent = ({ ref }: { ref: any }) => {
  const dispatch = useAppDispatch();

  const B2BInquiryList = useAppSelector(
    (state) => state.b2bInquiry.B2BInquiryList,
  );
  const loading = useAppSelector((state) => state.b2bInquiry.loading);
  const FilterB2BInquiry = useAppSelector(
    (state) => state.b2bInquiry.FilterB2BInquiry,
  );
  const currentPage = useAppSelector((state) => state.pagination.currentPage);
  const pageLimit = useAppSelector((state) => state.pagination.pageLimit);
  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);

  useEffect(() => {
    const payload = {
      ...FilterB2BInquiry,
      Limit: +pageLimit,
      Skip: +currentPage * +pageLimit,
    };
    dispatch(getAllB2BInquiries(payload));
  }, [currentPage, pageLimit]);

  useEffect(() => {
    return () => {
      dispatch(closeDialog());
      dispatch(setCurrentPage(0));
    };
  }, []);

  const pageChange = (event: GridPageChangeEvent) => {
    const newPage = event.page.skip / event.page.take;
    dispatch(setPageLimit(event.page.take));
    dispatch(setCurrentPage(newPage));
  };
  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${props.index === 11 ? "locked-header" : ""}`}
    >
      {props.children}
    </HeaderThElement>
  );
  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };
  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const AdvancePaymentFilterCell = React.useCallback(
    (props: GridCustomFilterCellProps) => (
      <CommonFilterCell
        {...props}
        filterSelector={(state) => state.b2bInquiry.FilterB2BInquiry}
        setFilterAction={setFilterB2BInquiry}
        clearFilterAction={clearFilterB2BInquiry}
        fetchDataAction={getAllB2BInquiries}
        pagination={{ case: "capitalize" }}
        // numberfiels={["Amount"]}
      />
    ),
    [],
  );

  const gridRef = useRef<any>(null);

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <ExcelExport
        data={B2BInquiryList}
        ref={ref}
        fileName={`B2B_Rate_Inquiries_${moment().format("DD-MM-YYYY hh-mm A")}.xlsx`}
      >
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 152px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          data={filter ? filterBy(B2BInquiryList, filter) : B2BInquiryList}
          onFilterChange={handleFilterChange}
          skip={currentPage * pageLimit}
          take={pageLimit}
          total={B2BInquiryList[0]?.ItemCount || B2BInquiryList?.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [10, 25, 50, 100, 500],
            pageSizeValue: pageLimit,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="CreatedOn"
            title="Date"
            width={130}
            filterable={false}
          />
          <Column
            field="Name"
            title="Customer Name"
            width={150}
            cells={{ filterCell: AdvancePaymentFilterCell }}
          />
          <Column
            field="MobileNo"
            title="Mobile No."
            width={150}
            cells={{ filterCell: AdvancePaymentFilterCell }}
          />
          <Column
            field="EmailID"
            title="Email ID"
            width={250}
            cells={{
              data: (props: any) => (
                <td className="text-break">{props.dataItem?.EmailID}</td>
              ),
              filterCell: AdvancePaymentFilterCell,
            }}
          />
          <Column
            field="ComponyName"
            title="Company"
            width={200}
            cells={{ filterCell: AdvancePaymentFilterCell }}
          />
          <Column
            field="CityName"
            title="City"
            width={150}
            cells={{ filterCell: AdvancePaymentFilterCell }}
          />
        </KendoGrid>
      </ExcelExport>
    </>
  );
};

export default B2BInquiry;
